import { CheckMark } from '@lyearn/icons';
import { QuizCreatorQuestionError } from '@/modules/corporate/modules/quiz/views/QuizEditor/hooks/useQuizEditor/types';
import { QuestionEditorViewModes } from '@/modules/question/types';
import {
  Maybe,
  QuestionInput,
  QuestionPerformanceType,
  QuestionSubmissionType,
  QuestionSubTypeEnum,
  QuestionType,
  QuestionTypeEnum,
  UpdateQuestionEntity,
} from '@/types/schema';

import { QuizPlayerQuestionContentFragment } from '../../quiz/views/QuizPlayer/graphql/fragments/generated/QuizPlayerQuestionContent';
import {
  CorrectAnswersType,
  QuestionAttemptType,
} from '../../quiz/views/QuizPlayer/views/QuizPlayerReview/components/QuizPlayerReviewContent/types';

export enum QuestionEvaluationEnum {
  Correct = 'CORRECT',
  InCorrect = 'IN_CORRECT',
  PartiallyCorrect = 'PARTIALLY_CORRECT',
}

export type QuizQuestionSubmissionType = Pick<
  QuestionSubmissionType,
  'optionResponse' | 'mtfResponse' | 'ftbResponse' | 'imageHotspotResponse'
>;

export interface GetQuestionEvaluationReturnType {
  evaluation: QuestionEvaluationEnum;
  count?: number;
}

export interface QuizPlayerFieldProps {
  question: QuizPlayerQuestionContentFragment;
  onChange?: (submission: QuizQuestionSubmissionType) => void;
  correctAnswers?: CorrectAnswersType;
  questionAttempt?: Pick<QuestionAttemptType, 'submission' | 'isSubmitted'>;
  classes?: { question?: string };
}

export interface QuestionReviewProps {
  question: QuizPlayerQuestionContentFragment;
  correctAnswers?: CorrectAnswersType;
  questionAttempt?: Pick<QuestionAttemptType, 'submission' | 'isSubmitted'>;
}

export interface QuestionEditorProps {
  viewMode: QuestionEditorViewModes;
  className?: string;
  error?: QuizCreatorQuestionError;
  questionLabel: string;
  question: UpdateQuestionEntity;
  isMultiSelect?: boolean;
  onChange: (question: UpdateQuestionEntity) => void;
  setNudgeState: (message: string) => void;
  variant?: 'survey' | 'quiz';
  isLocalizedContent?: boolean;
}

export interface ReviewQuestionListItemProps {
  index: number;
  question: QuestionType;
  performance?: QuestionPerformanceType;
  reviewAttemptId?: string;
}

export interface QuizViewField {
  Player: React.FC<QuizPlayerFieldProps>;
  type: QuestionTypeEnum;
  getQuestionLabel: ({
    questionSubType,
  }: {
    questionSubType?: QuestionSubTypeEnum | null;
  }) => string;
  labelKey: string;
  Icon: typeof CheckMark;
  getQuestionEvaluation: (performance: QuestionPerformanceType) => GetQuestionEvaluationReturnType;
  isValidSubmission: (
    question: QuestionType,
    submission?: Maybe<QuizQuestionSubmissionType>,
  ) => boolean;
  // Whether selecting an answer for this question type is a complete, unambiguous
  // response that can safely trigger auto-advance to the next question. Absent means false.
  getShouldAutoAdvance?: (question: QuestionType) => boolean;
  Review: React.FC<QuestionReviewProps>;
}

export interface QuizEditField {
  type: QuestionTypeEnum;
  Editor: React.FC<QuestionEditorProps>;
}

export interface QuizAIPreviewField {
  type: QuestionTypeEnum;
  Preview: React.FC<QuestionAIPreviewProps>;
}

export interface QuestionAIPreviewProps {
  questionLabel: string;
  question: QuestionInput;
  className?: string;
}

export type QuizField = QuizViewField & QuizEditField;
